"use client";

import { ensureTwindInstalled } from "./twind-client";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { ToastProvider } from "../components/ui/ToastProvider";
import { AppProvider } from "../components/app/AppProvider";
import { PwaRegistration } from "../components/app/PwaRegistration";
import { PwaInstallProvider } from "../components/app/pwa/PwaInstallProvider";
import OfflineOverlay from "../components/ui/OfflineOverlay";

function BootOverlay({ message }: { message: string }) {
  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 48%, #f0f9ff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ position: "relative", width: 96, height: 96, margin: "0 auto" }}>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 96,
                  height: 96,
                  border: "4px solid #ddd6fe",
                  borderRadius: 9999,
                  opacity: 0.75,
                  animation: "agiliza-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite",
                }}
              />
            </div>

            <div
              style={{
                position: "relative",
                width: 96,
                height: 96,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderTop: "4px solid #7c3aed",
                  borderBottom: "4px solid #7c3aed",
                  borderRadius: 9999,
                  animation: "agiliza-spin 1s linear infinite",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  width: 64,
                  height: 64,
                  borderRight: "4px solid #38bdf8",
                  borderLeft: "4px solid #38bdf8",
                  borderRadius: 9999,
                  animation: "agiliza-spin-reverse 1.5s linear infinite",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  width: 48,
                  height: 48,
                  background: "#ffffff",
                  borderRadius: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow:
                    "0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1)",
                  border: "1px solid #ddd6fe",
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/logo_complete_pack/logo.svg"
                  alt="Agiliza"
                  width={32}
                  height={32}
                  style={{ width: 32, height: 32, objectFit: "contain" }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 600,
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Agiliza
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#475569" }}>{message}</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes agiliza-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes agiliza-spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes agiliza-ping {
          75%,
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
}

function useTwindBootReady() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let raf = 0;
    let fallbackTimeout = 0;
    let finished = false;
    const startedAt = Date.now();

    const probe = document.createElement("div");

    const finish = () => {
      if (finished) return;
      finished = true;
      if (probe.parentNode) probe.parentNode.removeChild(probe);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(fallbackTimeout);
      setReady(true);
    };

    try {
      ensureTwindInstalled();

      probe.className = "hidden";
      probe.style.position = "absolute";
      probe.style.opacity = "0";
      probe.style.pointerEvents = "none";
      document.body.appendChild(probe);
    } catch {
      finish();
      return;
    }

    const check = () => {
      const elapsed = Date.now() - startedAt;
      const display = window.getComputedStyle(probe).display;
      const twindApplied = display === "none";

      if (twindApplied || elapsed > 1200) {
        finish();
        return;
      }

      raf = window.requestAnimationFrame(check);
    };

    raf = window.requestAnimationFrame(check);
    fallbackTimeout = window.setTimeout(finish, 1500);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(fallbackTimeout);
      if (probe.parentNode) probe.parentNode.removeChild(probe);
    };
  }, []);

  return ready;
}

export function Providers({ children }: { children: ReactNode }) {
  const twindReady = useTwindBootReady();

  /*
   * O cartao de tela cheia so cobre a primeira carga, enquanto o Twind ainda
   * nao aplicou os estilos — sem ele a pagina apareceria crua por um instante.
   *
   * Antes ele tambem subia a cada troca de rota, por 280ms fixos. Como e
   * `position: fixed; inset: 0; z-index: 9998`, cobria a aplicacao inteira:
   * clicar em qualquer item do menu lateral fazia a tela toda virar o logo da
   * Agiliza girando com "Carregando pagina...", a barra lateral sumia de vista
   * e so voltava quando o tempo acabava. Quem navega ja tem a casca na tela; o
   * sinal de carregamento certo e o da area de conteudo, em
   * `app/admin/loading.tsx`, que nao esconde o menu.
   */
  return (
    <ToastProvider>
      <AppProvider>
        <PwaInstallProvider>
          <PwaRegistration />
          <OfflineOverlay />
          {children}
          {!twindReady ? <BootOverlay message="Carregando sistema..." /> : null}
        </PwaInstallProvider>
      </AppProvider>
    </ToastProvider>
  );
}
